import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { chListEmpresas, chGetMeta, chCreateEmpresa, chDeleteEmpresa } from "@/lib/prevelis";
import { toast } from "sonner";
import { Plus, Building2, ChevronRight, X, MapPin, Trash2 } from "lucide-react";

const field = "w-full h-11 px-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none";

export default function SuizaEmpresas() {
  const [empresas, setEmpresas] = useState([]);
  const [meta, setMeta] = useState({ cantones: [], tipos_organizacion: [] });
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ razon_social: "", canton: "", actividad: "", num_trabajadores: "", tipo_organizacion: "" });
  const navigate = useNavigate();

  const load = () => chListEmpresas().then(setEmpresas).catch(() => {});
  useEffect(() => { load(); chGetMeta().then(setMeta).catch(() => {}); }, []);

  const borrar = async (e, empresa) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar la empresa "${empresa.razon_social}"? Se borrarán también sus documentos y cuestionarios.`)) return;
    try { await chDeleteEmpresa(empresa.id); await load(); toast.success("Empresa eliminada"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const crear = async () => {
    if (!form.razon_social.trim()) { toast.error("Indica la razón social"); return; }
    try {
      const e = await chCreateEmpresa(form);
      toast.success("Empresa creada");
      setDialog(false);
      setForm({ razon_social: "", canton: "", actividad: "", num_trabajadores: "", tipo_organizacion: "" });
      navigate(`/ch/empresas/${e.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo crear"); }
  };

  return (
    <Layout title="Empresas · Suiza 🇨🇭" subtitle="Cantones francófonos · documentación preventiva generada por IA"
      actions={
        <button onClick={() => setDialog(true)} data-testid="ch-nueva-empresa" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800">
          <Plus className="w-5 h-5" /> <span className="hidden sm:inline">Nueva empresa</span>
        </button>
      }
    >
      {empresas.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-500" data-testid="ch-empresas-vacio">
          Aún no hay empresas suizas. Crea la primera para generar su documentación preventiva.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ch-empresas-lista">
          {empresas.map((e) => (
            <div key={e.id} data-testid={`ch-empresa-${e.id}`} onClick={() => navigate(`/ch/empresas/${e.id}`)} role="button" tabIndex={0}
              className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 transition-colors flex items-center gap-4 cursor-pointer">
              <div className="w-11 h-11 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0"><Building2 className="w-5 h-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-semibold text-slate-900 truncate">{e.razon_social}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.canton} · {e.actividad || "—"}</div>
              </div>
              <button onClick={(ev) => borrar(ev, e)} data-testid={`ch-empresa-borrar-${e.id}`} title="Eliminar empresa"
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
              <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDialog(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="ch-empresa-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">Nueva empresa (Suiza)</h3>
              <button onClick={() => setDialog(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="text-sm font-medium text-slate-700">Razón social *</label>
                <input data-testid="ch-form-razon" value={form.razon_social} onChange={(e) => setF("razon_social", e.target.value)} className={field + " mt-1"} /></div>
              <div><label className="text-sm font-medium text-slate-700">Cantón</label>
                <select data-testid="ch-form-canton" value={form.canton} onChange={(e) => setF("canton", e.target.value)} className={field + " mt-1"}>
                  {meta.cantones.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-sm font-medium text-slate-700">Actividad</label>
                <input data-testid="ch-form-actividad" value={form.actividad} onChange={(e) => setF("actividad", e.target.value)} placeholder="Ej. Panadería, taller mecánico, restaurante…" className={field + " mt-1"} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700">Nº trabajadores</label>
                  <input type="number" min="0" data-testid="ch-form-num" value={form.num_trabajadores} onChange={(e) => setF("num_trabajadores", e.target.value)} className={field + " mt-1"} /></div>
                <div><label className="text-sm font-medium text-slate-700">Tipo</label>
                  <select data-testid="ch-form-tipo" value={form.tipo_organizacion} onChange={(e) => setF("tipo_organizacion", e.target.value)} className={field + " mt-1"}>
                    {meta.tipos_organizacion.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setDialog(false)} className="h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={crear} data-testid="ch-form-guardar" className="h-11 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800">Crear</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
